import React from "react";
import { amountLabel, count, minutes, rupeesWhole } from "../format";

export default function ChannelBreakdown({ breakdown, deliveryData, amount = "food" }) {
  const fulfilmentRows = (breakdown && breakdown.fulfilment) || [];
  const totalFulfilmentOrders = fulfilmentRows.reduce((sum, r) => sum + (r.orders || 0), 0);

  const getFulfilmentSegment = (key) =>
    fulfilmentRows.find((r) => r.key === key) || { orders: 0, revenue: 0 };

  const deliveryRow = getFulfilmentSegment("DELIVERY");
  const pickupRow = getFulfilmentSegment("SELF_PICKUP");
  const otherOrders = totalFulfilmentOrders - (deliveryRow.orders + pickupRow.orders);

  const share = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);
  const delivPct = share(deliveryRow.orders, totalFulfilmentOrders);
  const pickPct = share(pickupRow.orders, totalFulfilmentOrders);
  const otherPct = share(otherOrders, totalFulfilmentOrders);

  // Donut circumference offsets (for r=15.9155, C=100)
  const delivDash = `${delivPct} 100`;
  const pickDash = `${pickPct} 100`;
  const pickOffset = -delivPct;
  const otherDash = `${otherPct} 100`;
  const otherOffset = -(delivPct + pickPct);

  const paymentRows = (breakdown && breakdown.payment_mode) || [];
  const totalPaymentOrders = paymentRows.reduce((sum, r) => sum + (r.orders || 0), 0);

  const getPayment = (key) =>
    paymentRows.find((r) => r.key === key) || { orders: 0, revenue: 0 };

  const upiRow = getPayment("ONLINE");
  const otherPayRow = getPayment("OTHER");
  const codRow = getPayment("COD");

  const upiPct = share(upiRow.orders, totalPaymentOrders);
  const otherPayPct = share(otherPayRow.orders, totalPaymentOrders);
  const codPct = share(codRow.orders, totalPaymentOrders);

  const legs = (deliveryData && deliveryData.legs) || {};

  return (
    <section className="ins-section">
      <div className="ins-row-triplet">
        <div className="ins-card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div className="ins-card-header">
            <h3 className="ins-card-title">Orders by order type</h3>
            <span className="material-symbols-outlined" style={{ color: "#94a3b8" }}>pie_chart</span>
          </div>
          <div className="ins-card-body">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", flexWrap: "wrap", gap: "1rem" }}>
              <div className="ins-donut-wrap">
                <svg className="w-full h-full" viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)", width: "100%", height: "100%" }}>
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#f1f5f9"
                    strokeWidth="4.5"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#fb310d"
                    strokeDasharray={delivDash}
                    strokeLinecap="round"
                    strokeWidth="4.5"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#4648d4"
                    strokeDasharray={pickDash}
                    strokeDashoffset={pickOffset}
                    strokeLinecap="round"
                    strokeWidth="4.5"
                  />
                  {otherPct > 0 && (
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#ff7e2d"
                      strokeDasharray={otherDash}
                      strokeDashoffset={otherOffset}
                      strokeLinecap="round"
                      strokeWidth="4.5"
                    />
                  )}
                </svg>
                <div className="ins-donut-center">
                  <span className="ins-donut-center-sub">Total</span>
                  <span className="ins-donut-center-val">{count(totalFulfilmentOrders)}</span>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", fontSize: "0.875rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "9999px", backgroundColor: "#fb310d" }} />
                  <span style={{ color: "#475569" }}>
                    Delivery: <strong style={{ color: "#0f172a" }}>{delivPct}%</strong> ({count(deliveryRow.orders)})
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "9999px", backgroundColor: "#4648d4" }} />
                  <span style={{ color: "#475569" }}>
                    Pickup: <strong style={{ color: "#0f172a" }}>{pickPct}%</strong> ({count(pickupRow.orders)})
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "9999px", backgroundColor: "#ff7e2d" }} />
                  <span style={{ color: "#475569" }}>
                    Other: <strong style={{ color: "#0f172a" }}>{otherPct}%</strong> ({count(otherOrders)})
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ padding: "0.75rem 1.25rem", borderTop: "1px solid #f1f5f9", fontSize: "0.8125rem", color: "#94a3b8" }}>
            Share of orders placed in the range
          </div>
        </div>

        <div className="ins-card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div className="ins-card-header">
            <h3 className="ins-card-title">Orders by payment</h3>
            <span className="material-symbols-outlined" style={{ color: "#94a3b8" }}>payments</span>
          </div>
          <div className="ins-card-body">
            <div className="ins-stacked-bar">
              <div style={{ width: `${upiPct}%`, backgroundColor: "#059669" }} title={`Online (${upiPct}%)`} />
              <div style={{ width: `${otherPayPct}%`, backgroundColor: "#4f46e5" }} title={`Wallet / other (${otherPayPct}%)`} />
              <div style={{ width: `${codPct}%`, backgroundColor: "#f59e0b" }} title={`Cash on Delivery (${codPct}%)`} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.875rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#059669" }} />
                  <span style={{ color: "#334155", fontWeight: 500 }}>Online (card, UPI, netbanking)</span>
                </div>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>
                  {upiPct}% <span style={{ color: "#94a3b8", fontWeight: 400 }}>({rupeesWhole(upiRow.revenue)})</span>
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.875rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#4f46e5" }} />
                  <span style={{ color: "#334155", fontWeight: 500 }}>Wallet / other (no gateway payment)</span>
                </div>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>
                  {otherPayPct}% <span style={{ color: "#94a3b8", fontWeight: 400 }}>({rupeesWhole(otherPayRow.revenue)})</span>
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.875rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#f59e0b" }} />
                  <span style={{ color: "#334155", fontWeight: 500 }}>Cash on Delivery (COD)</span>
                </div>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>
                  {codPct}% <span style={{ color: "#94a3b8", fontWeight: 400 }}>({rupeesWhole(codRow.revenue)})</span>
                </span>
              </div>
            </div>
          </div>
          <div style={{ padding: "0.75rem 1.25rem", borderTop: "1px solid #f1f5f9", fontSize: "0.8125rem", color: "#94a3b8" }}>
            {amountLabel(amount)} by payment mode
          </div>
        </div>

        <div className="ins-card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div className="ins-card-header">
            <div>
              <h3 className="ins-card-title">Delivery &amp; Service Time</h3>
              <p className="ins-card-subtitle">
                Averages over {count(legs.orders_measured || 0)} delivered orders
              </p>
            </div>
            <span className="material-symbols-outlined" style={{ color: "#94a3b8" }}>schedule</span>
          </div>
          <div className="ins-card-body">
            <div className="ins-milestones-grid">
              <div className="ins-milestone-box">
                <span className="ins-milestone-lbl">Store accepts</span>
                <div className="ins-milestone-val" style={{ color: "#047857" }}>
                  {minutes(legs.accept_minutes)}
                </div>
                <span className="ins-milestone-tag" style={{ color: "#64748b" }}>placed → accepted</span>
              </div>
              <div className="ins-milestone-box border-mid">
                <span className="ins-milestone-lbl">Kitchen preps</span>
                <div className="ins-milestone-val">
                  {minutes(legs.prep_minutes)}
                </div>
                <span className="ins-milestone-tag" style={{ color: "#64748b" }}>accepted → picked up</span>
              </div>
              <div className="ins-milestone-box">
                <span className="ins-milestone-lbl">Rider delivers</span>
                <div className="ins-milestone-val" style={{ color: "#4648d4" }}>
                  {minutes(legs.rider_minutes)}
                </div>
                <span className="ins-milestone-tag" style={{ color: "#64748b" }}>picked up → delivered</span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "0.5rem" }}>
              <span style={{ fontSize: "0.875rem", color: "#64748b", fontWeight: 500 }}>Total Customer Wait:</span>
              <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize: "1.25rem", fontWeight: 800, color: "#fb310d" }}>
                {minutes(legs.wait_minutes)} avg
              </span>
            </div>
          </div>
          <div style={{ padding: "0.75rem 1.25rem", borderTop: "1px solid #f1f5f9", fontSize: "0.8125rem", color: "#94a3b8" }}>
            Self-pickup and scheduled orders are excluded
          </div>
        </div>
      </div>
    </section>
  );
}
